import React from 'react';

import type { VariantProp } from '../../common/colors';
import type { SpacingScale } from '../../utilities/shared/sizes';
import type { IconName } from '../Icon/Icon';
import { colors, styled } from '../../stitches.config';
import { useSurfaceVariant } from '../../utilities/useSurfaceVariant';
import { AssetTile } from '../AssetTile/AssetTile';
import { Icon } from '../Icon/Icon';

const FramedIconLiteral = styled(Icon, {
  display: 'flex',
  width: '100%',
  height: '100%',
  color: colors.iconNeutralLight,

  variants: {
    variant: {
      alternative: { color: colors.iconAlternativeLight },
      attention: { color: colors.iconAttentionLight },
      brand: { color: colors.iconBrandLight },
      neutral: { color: colors.iconNeutralLight },
      negative: { color: colors.iconNegativeLight },
      positive: { color: colors.iconPositiveLight },
    },
    internal: {
      true: { color: colors.internalIconLight },
      false: {},
    },
  },
});
FramedIconLiteral.displayName = 'FramedIconLiteral';

export interface FramedIconProps {
  /**
   * Set which icon to display.
   */
  icon: IconName;
  /**
   * Set whether the icon is internal.
   */
  internal?: boolean;
  /**
   * Set a size of the component.
   */
  size?: SpacingScale;
  /**
   * Optional override for the variant. When not provided, the variant is
   * automatically detected from the background of the parent element.
   */
  variant?: VariantProp;
}

export function FramedIcon({
  icon,
  internal,
  size = 28,
  variant: variantProp,
  ...remaining
}: FramedIconProps) {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const detectedVariant = useSurfaceVariant(containerRef);
  const variant = variantProp ?? detectedVariant;

  return (
    <AssetTile ref={containerRef} size={size} {...remaining}>
      <FramedIconLiteral icon={icon} internal={internal} size={size} variant={variant} />
    </AssetTile>
  );
}
